import { ReactNode } from "react";
import { Page404 } from "design-system/components/pages/page-404/page-404";
import { createSiteHeaderProps } from "frontend/contentful/components/universal/site-header.props";
import { createSiteFooterProps } from "frontend/contentful/components/universal/site-footer.props";
import { useContentful } from "frontend/hooks/use-contentful";
import { ContentfulRichText } from "../blocks/rich-text.contentful";
import { usePageProps } from "frontend/hooks/use-page-props";
import { ContentfulHead } from "../primitives/head.contentful";

type ErrorPageOverrideContent = {
  pageTitle?: string;
  title: string;
  body: ReactNode;
};

export type ContentfulErrorPageProps = {
  override?: ErrorPageOverrideContent;
};

// assumes it is wrapped in the PagePropsProvider
export function ContentfulErrorPage({ override }: ContentfulErrorPageProps) {
  const { error404Settings } = usePageProps();
  const { site, getEntry, getEntries } = useContentful();

  const headerBase = site ? createSiteHeaderProps(site) : undefined;
  const footer = site?.fields?.footer
    ? createSiteFooterProps(site.fields.footer)
    : undefined;
  const settings = getEntry(error404Settings);
  const contentfulBody = getEntries(settings?.fields.body);

  // Only override the title if we actually have one from override or settings otherwise let Page404 use its own default.
  const title = override?.title ?? settings?.fields?.title;

  // Only override the children/body if we have something otherwise let Page404 use its own default children.
  const body: ReactNode =
    override?.body ??
    (contentfulBody
      ? contentfulBody.map((entry, i) => (
          <ContentfulRichText key={i} entry={entry} />
        ))
      : undefined);

  const header =
    headerBase &&
    ({
      name: "SiteHeader",
      props: {
        ...headerBase,
        theme:
          headerBase.theme && headerBase.theme !== "dark"
            ? headerBase.theme
            : "light",
      },
    } as const);

  return (
    <>
      <ContentfulHead
        title={
          (override?.pageTitle ? override?.pageTitle : "Page not found") +
          " | Harvard Business School"
        }
      />
      <Page404 theme="light" header={header} footer={footer} title={title}>
        {body}
      </Page404>
    </>
  );
}
